import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    createNonStandardFormDataSuccess,
    saveCompletionSummarySuccess,
    updateNonStandardFormDataSuccess,
    updateStandardFormDataSuccess,
} from '@wdx/shared/infrastructure/form-framework';
import { AllSignalrEvents, SignalrEvent } from '@wdx/shared/utils';
import { signalrHubUnstarted, switchMapHubToAction } from 'ngrx-signalr-core';
import { EMPTY, merge, of } from 'rxjs';
import { catchError, groupBy, map, mergeMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as documentTypesActions from '../../state/document-types/document-types.actions';
import * as appActions from '../apps/apps.actions';
import * as documentTemplatesActions from '../document-templates/document-templates.actions';
import * as documentsActions from '../documents/documents.actions';
import {
    SignalrConnectionAction,
    newSystemEvent,
    newSystemSignalrEvent,
    registerSystemSignalrEvents,
    systemSignalrEventsRegistered,
} from './system-events.actions';

@Injectable()
export class SystemEventsEffects {
    private actions$ = inject(Actions);
    private signalrEventId = 0;

    /**
     * @deprecated for form events use DynamicFormsFacade.getFormFrameworkEvents$ instead.
     * For other events send the event from the source instead of listening to actions here
     * e.g. example in AssignEffects.assignToSuccess$
     */
    captureEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                saveCompletionSummarySuccess,
                appActions.confirmSuccess,
                documentsActions.deleteDocumentSuccess,
                documentTemplatesActions.generateDocumentTemplateSuccess,
                documentTypesActions.deleteDocumentTypesSuccess,
                documentTypesActions.disableDocumentTypeSuccess,
                createNonStandardFormDataSuccess,
                updateNonStandardFormDataSuccess,
                updateStandardFormDataSuccess,
            ),
            map((action) =>
                newSystemEvent({
                    event: { sourceAction: action, timeStamp: Date.now() },
                }),
            ),
        ),
    );

    captureSignalrEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(signalrHubUnstarted, registerSystemSignalrEvents),
            groupBy((action) => `${action.hubName}|${action.url}`),
            mergeMap((hubActions$) =>
                hubActions$.pipe(
                    switchMapHubToAction(({ hub, action }) => {
                        const connectionAction = getConnectionAction(action);
                        const signalrActions = AllSignalrEvents.map(
                            (signalrEvent) =>
                                hub.on<unknown>(signalrEvent).pipe(
                                    map((data) =>
                                        this.toSignalrAction(
                                            signalrEvent,
                                            data,
                                        ),
                                    ),
                                    catchError(() => EMPTY),
                                ),
                        );

                        return merge(
                            of(
                                systemSignalrEventsRegistered({
                                    hubName: action.hubName,
                                    url: action.url,
                                    connectionAction,
                                }),
                            ),
                            ...signalrActions,
                        );
                    }),
                ),
            ),
        ),
    );

    private toSignalrAction(signalrEvent: SignalrEvent, data: unknown) {
        if (!environment.production) {
            // eslint-disable-next-line no-restricted-syntax
            console.debug(
                `(${new Date().toTimeString().split(' ')?.[0]}) SignalR Message:`,
                signalrEvent,
                data,
            );
        }

        return newSystemSignalrEvent({
            event: {
                eventId: ++this.signalrEventId,
                signalrEvent,
                timeStamp: Date.now(),
                data,
            },
        });
    }
}

function getConnectionAction(action: {
    connectionAction?: SignalrConnectionAction;
}): SignalrConnectionAction {
    return action.connectionAction || 'start';
}
